<template>
  <div>
    <validation-observer ref="observer">
      <v-container>
        <v-card class="pa-8 mx-auto" max-width="900">
          <!-- タイトル -->
          <v-row class="mb-6">
          <v-col class="text-center">
            <h2 class="text-h5 font-weight-bold mb-2">
              JWNET加入情報を利用した申込
            </h2>
            <h3 class="text-h6">
              既加入者向け申込フォーム
            </h3>
          </v-col>
        </v-row>
        <!-- 説明文 -->
        <v-row class="mb-6">
          <v-col>
            <p class="mb-2">
              既にJWNETに加入済みの方が、DXE株式会社のEDI連携サービスを利用するための申込フォームです。
            </p>
          </v-col>
        </v-row>
        <v-form ref="form" v-model="valid">
          <!-- JWNET登録事業者名 -->
           <v-row align="center">
            <v-col cols="12" md="3">
              <strong>JWNET登録事業者名</strong>
            </v-col>
            <v-col cols="12" md="9" class="mt-0 pa-0">
              <span class="text-body-1">{{ jwnetName }}</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="3"></v-col>
            <v-col cols="12" md="9" class="mt-0 pa-0">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true }"
                name="JWNET登録事業者名"
                class="requiredMark"
              >
                <v-select
                  v-model="form.corporation_code"
                  :items="corporationTypes"
                  :error-messages="errors"
                  outlined
                  dense
                  label=" "
                  placeholder="選択してください"
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 事業者名の表示順 -->
          <v-row>
            <v-col cols="12" md="3"></v-col>
            <v-col cols="12" md="9" class="mt-0 pa-0">
              <v-radio-group
                v-model="form.corporate_designation_type"
                :disabled="isFixedCorporationType"
                row
              >
                <v-radio label="事業者名の前に表示" value="position_before" />
                <v-radio label="事業者名の後ろに表示" value="position_after" />
              </v-radio-group>
            </v-col>
          </v-row>
          <!-- 事業者名 -->
          <v-row>
            <v-col cols="12" md="3"><strong>事業者名</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true,max: 60 }"
                name="事業者名"
                class="requiredMark"
              >
                <v-text-field
                  v-model="form.emission_name"
                  :error-messages="errors"
                  outlined
                  dense
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.emission_name?.length || 0 }} / 60)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 事業者名（フリガナ） -->
          <v-row>
            <v-col cols="12" md="3"><strong>事業者名（フリガナ）</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ katakana_only: true, max: 60 }"
                name="事業者名フリガナ"
              >
                <v-text-field
                  v-model="form.emission_name_kana"
                  :error-messages="errors"
                  outlined
                  dense
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.emission_name_kana?.length || 0 }} / 60)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 郵便番号 -->
          <v-row>
            <v-col cols="12" md="3"><strong>郵便番号</strong></v-col>
            <v-col cols="12" md="4">
             <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, zip_code: true, max: 8 }"
                name="郵便番号"
                class="requiredMark">
                <v-text-field
                  v-model="form.zip_code"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="100-0005"
                  persistent-placeholder
                  hint="ハイフンを含めて入力してください"
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.zip_code?.length || 0 }} / 8)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <zip-code-search
                :zipCode="form.zip_code"
                @zipAddress="setZipAddress"
              />
              &nbsp;
              <v-btn
                outlined
                small
                @click="searchZipButtonClickEvent"
              >
                住所→郵便番号
              </v-btn>
            </v-col>
          </v-row>
          <!-- 都道府県・市区町村 -->
          <v-row>
            <v-col cols="12" md="3"><strong>都道府県</strong></v-col>
            <v-col cols="12" md="4">
              <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true }"
                  class="requiredMark"
                  name="都道府県"
                >
                  <v-autocomplete
                    v-model="form.prefecture_code"
                    :error-messages="errors"
                    :items="prefList"
                    dense
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    outlined
                    label=" "
                    persistent-placeholder
                    placeholder="例）東京都"
                  >
                  </v-autocomplete>
                </validation-provider>
            </v-col>
            <v-col cols="12" md="1" class="d-flex justify-center"><strong>市区町村</strong></v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, max: 20 }"
                name="市区町村"
                class="requiredMark">
                <v-text-field
                  v-model="form.address_1"
                  :error-messages="errors"
                  outlined
                  dense
                  persistent-placeholder
                  placeholder="例）中央区"
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.address_1?.length || 0 }} / 20)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>

            </v-col>
          </v-row>
          <!-- 町域 -->
          <v-row>
            <v-col cols="12" md="3"><strong>町域</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ max: 40 }"
                name="町域"
              >
                <v-text-field
                  v-model="form.address_2"
                  :error-messages="errors"
                  outlined
                  dense
                  persistent-placeholder
                  placeholder="例）日本橋兜町"
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.address_2?.length || 0 }} / 40)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <!-- 詳細住所 -->
          <v-row>
            <v-col cols="12" md="3">
              <strong>詳細住所</strong>
            </v-col>
            <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ max: 40 }"
                  name="詳細住所"
                >
                  <v-text-field
                    v-model="form.address_3"
                    :error-messages="errors"
                    outlined
                    dense
                    persistent-placeholder
                    placeholder="例）１−２−３　○○ビル○階"
                  >
                    <template v-slot:label>
                      <span class="ml-1">
                        ({{ form.address_3?.length || 0 }} / 40)
                      </span>
                    </template>
                  </v-text-field>
                </validation-provider>
            </v-col>
          </v-row>

          <!-- ご担当者名 -->
          <v-row>
            <v-col cols="12" md="3">
              <strong>ご担当者名</strong>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, max: 10 }"
                name="ご担当者姓"
                class="requiredMark">
                <v-text-field
                  v-model="form.contact_last_name"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="姓"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.contact_last_name?.length || 0 }} / 10)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, max: 10 }"
                name="ご担当者名"
                class="requiredMark">
                <v-text-field
                  v-model="form.contact_first_name"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="名"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.contact_first_name?.length || 0 }} / 10)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <!-- ご担当者名（フリガナ） -->
          <v-row>
            <v-col cols="12" md="3">
              <strong>ご担当者名（フリガナ）</strong>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ katakana_only: true, max: 20 }"
                name="ご担当者姓カナ"
              >
                <v-text-field
                  v-model="form.contact_last_name_kana"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="セイ"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.contact_last_name_kana?.length || 0 }} / 20)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ katakana_only: true, max: 20 }"
                name="ご担当者名カナ"
              >
                <v-text-field
                  v-model="form.contact_first_name_kana"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="メイ"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.contact_first_name_kana?.length || 0 }} / 20)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>


          <!-- メールアドレス -->
          <v-row>
            <v-col cols="12" md="3">
              <strong>メールアドレス</strong>
            </v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, email: true, max: 255 }"
                name="メールアドレス"
                class="requiredMark"
              >
                <v-text-field
                  v-model="form.email"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="emission@dx-e.jp"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.email?.length || 0 }} / 255)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
              <p class="red--text text-caption">
                ※　登録されたメールアドレスは、JWセンターからの手続きやシステム等（ログインパスワード）に関する重要なお知らせの配信先となります。
              </p>
            </v-col>
          </v-row>

          <!-- 処理業者 -->
          <v-row class="mb-6">
            <v-col cols="12" md="3">
              <strong>処理業者</strong>
            </v-col>
            <v-col cols="12" md="9" class="align-center">
              <span class="text-body-1">
                {{ form.transport_name || '未設定' }}
              </span>
            </v-col>
          </v-row>

          <!-- JWNET料金区分 -->
          <v-row>
            <v-col cols="12" md="3"><strong>JWNET料金区分</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true }"
                name="JWNET料金区分"
                class="requiredMark"
              >
                <v-select
                  v-model="form.jwnet_pricing_type"
                  :items="feeTypes"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="選択してください"
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 加入者番号 -->
          <v-row>
            <v-col cols="12" md="3"><strong>加入者番号</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, alpha_num_only: true, length: 7 }"
                name="加入者番号"
                class="requiredMark"
              >
                <v-text-field
                  v-model="form.subscriber_number"
                  :error-messages="errors"
                  outlined
                  dense
                  placeholder="1から始まる7桁の数字"
                  hint="※加入者番号は1から始まる7桁の数字"
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.subscriber_number?.length || 0 }} / 7)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- EDI利用確認キー -->
          <v-row>
            <v-col cols="12" md="3"><strong>EDI利用確認キー</strong></v-col>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, numeric: true, length: 8 }"
                name="EDI利用確認キー"
                class="requiredMark"

              >
                <v-text-field
                  v-model="form.edi_usage_key"
                  :error-messages="errors"
                  outlined
                  dense
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span class="ml-1">
                      ({{ form.edi_usage_key?.length || 0 }} / 8)
                    </span>
                  </template>
                </v-text-field>
              </validation-provider>
              <a href="https://dx-e.net/wp-content/uploads/2025/03/JWNET-How-to-check.pdf" target="_blank" class="blue--text text-caption">加入者番号・EDI利用確認キーの確認方法はこちら</a>
            </v-col>
          </v-row>
          <!-- ボタン -->
          <v-row class="mt-8">
            <v-col cols="6">
              <v-btn
                block
                large
                outlined
                @click="goBack"
              >
                戻る
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                large
                color="primary"
                :disabled="isValidating"
                :loading="isValidating"
                @click="submit"
              >
                確認画面へ進む
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>
    </validation-observer>
    <search-zip-modal-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="form.prefecture_code"
      :initial-city-name="form.address_1"
      :initial-town-name="form.address_2"
      @reflectFullCallback="reflectFullCallback"
      @reflectZipCallback="reflectZipCallback"
      @closeCallback="closeModal"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import PrefectureList from '@/mixins/PrefectureList'
import ZipCodeSearch from '@/components/ZipCodeSearch'
import SearchZipModalModal from "@/driver/components/SearchZipModal.vue";
import CorporationTypeMixin from "@/mixins/CorporationTypeMixin";

export default {
  name: 'RegistrationFormSubscribed',
  components: {
    SearchZipModalModal,
    ZipCodeSearch
  },
  mixins: [PrefectureList, CorporationTypeMixin],
  props: {
    formData: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      valid: true,
      isValidating: false,
      form: this.formData ? { ...this.formData } : {
        jwnet_name: null,
        corporation_code: null,
        corporate_designation_type: 'position_before',
        emission_name: null,
        emission_name_kana: null,
        zip_code: null,
        prefecture_code: null,
        address_1: null,
        address_2: null,
        address_3: null,
        contact_last_name: null,
        contact_first_name: null,
        contact_last_name_kana: null,
        contact_first_name_kana: null,
        email: null,
        jwnet_pricing_type: null,
        subscriber_number: null,
        edi_usage_key: null,
      },
      feeTypes: [
        { value: 'a_plan', text: 'A料金' },
        { value: 'b_plan', text: 'B料金' },
        { value: 'c_plan', text: 'C料金' },
        { value: 'none_plan', text: '不明' }
      ],
      enableSearchZipModal: false,
    }
  },
  computed: {
    prefectures() {
      return this.prefList.map(pref => pref.text)
    },
    jwnetName() {
      const org = this.getCorporationTypeText(this.form.corporation_code) || ''
      const name = this.form.emission_name || ''
      let jwnetName = ''
      if (this.isFixedCorporationType) {
        jwnetName = `${name}`.trim()
      } else if (this.form.corporate_designation_type === 'position_before') {
        jwnetName = `${org}${name}`.trim()
      } else {
        jwnetName = `${name}${org}`.trim()
      }
      return jwnetName
    },
    isFixedCorporationType () {
      return ['00', '99', '999'].includes(this.form.corporation_code);
    },
  },
  watch: {
    isFixedCorporationType (value) {
      if (value ) {
        this.form.corporate_designation_type = 'position_before';
      }
    },
    formData: {
      handler(newData) {
        if (newData) {
          this.form = { ...newData }
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('EdiRequestSubscribed', {
      validateRequest: 'validate',
      validateEditRequest: 'validateEdit',
    }),
    ...mapActions('snackbar', {
      showError: 'showError'
    }),
    getCorporationTypeText(code) {
      const corporation_type = this.corporationTypeBy(code);
      return corporation_type ? corporation_type.text : ''
    },
    setZipAddress(content) {
      const prefecture = this.prefList.find(p => p.text === content.prefecture || p.value == String(content.pref_code));
      if (prefecture) {
        this.form.prefecture_code = prefecture.value;
      }
      this.form.address_1 = content.locality;
      this.form.address_2 = content.street;
    },
    goBack() {
      // 編集モードの場合はAdminEdiRequestsに戻る
      if (this.isNew) {
        this.$router.push({ name: 'EdiRequestRegistrations', params: { token: this.$route.params.token } })
      } else {
        this.$router.push({ name: 'AdminEdiRequests' })
      }
    },
    submit() {
      let $self = this
      this.$refs.observer.validate().then((result) => {
        if (result) {
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation() {
      this.isValidating = true
      this.form.jwnet_name = this.jwnetName
      const formData = { ...this.form }
      
      const payload = {
        emission_request: formData
      }

      if (this.isNew) {
        // 新規登録の場合
        const token = this.$route.params.token
        this.validateRequest({ token, data: payload }).then(() => {
          this.form = { ...formData }
          this.$emit('submit', formData)
        }).catch((error) => {
          this.handleValidationError(error)
        }).finally(() => {
          this.isValidating = false
        })
      } else {
        // 編集の場合
        const id = this.form.id
        this.validateEditRequest({ id, data: payload }).then(() => {
          this.form = { ...formData }
          this.$emit('submit', formData)
        }).catch((error) => {
          this.handleValidationError(error)
        }).finally(() => {
          this.isValidating = false
        })
      }
    },
    handleValidationError(error) {
      // サーバーサイドのバリデーションエラーを処理
      if (error.response && error.response.data && error.response.data.errors) {
        // エラーをvee-validateのフォーマットに変換
        const errors = {}
        const serverErrors = error.response.data.errors
        
        // エラーメッセージをフィールド名にマッピング
        Object.keys(serverErrors).forEach(key => {
          // emission_request.field_name の形式から field_name を抽出
          const fieldName = key.replace('emission_request.', '')
          // 配列の場合は最初のメッセージを取得、文字列の場合はそのまま使用
          const errorMessage = Array.isArray(serverErrors[key]) 
            ? serverErrors[key][0] 
            : serverErrors[key]
          errors[fieldName] = errorMessage
        })
        
        // validation-observerにエラーをセット
        this.$refs.observer.setErrors(errors)
        
        // スクロールして最初のエラーフィールドを表示
        this.$nextTick(() => {
          this.$vuetify.goTo(0)
        })
      } else {
        // 汎用エラーメッセージを表示
        const errorMessage = error.response?.data?.result?.message 
          || error.response?.data?.message 
          || 'バリデーションエラーが発生しました。入力内容をご確認ください。'
        this.showError({ message: errorMessage })
      }
    },
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    closeModal () {
      this.enableSearchZipModal = false;
    },
    reflectFullCallback (address) {
      this.form.zip_code = address.zip_code;
      this.form.prefecture_code = `${address.prefecture_code}`;
      this.form.address_1 = address.city ? address.city : '';
      this.form.address_2 = address.town ? address.town : '';
    },
    reflectZipCallback (zipCode) {
      this.form.zip_code = zipCode
    },
  }
}
</script>
